import { withNamespaces } from "react-i18next";
import paginationFactory, {
    PaginationListStandalone,
    PaginationProvider,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from "react-bootstrap-table2-paginator";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from "react-bootstrap-table2-overlay";
import { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { API_URL_DOWNLOAD_APP_DOCUMENTS } from "src/modules/data-protection/constants";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "@tanstack/react-query";
import ControlService from "src/modules/data-protection/apis/ControlService";
import { Link } from "react-router-dom";
import { Button, Col, Progress, Row, Spinner, UncontrolledTooltip } from "reactstrap";
import { formatTaskAndRiskId } from "src/modules/data-protection/constants";
import { RiEdit2Line } from "react-icons/ri";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import SOAPdfExport from "../export/PDF";
import ExportWaitingModal from "../../../components/ExportWaitingModal";
import JustificationModal from "../../../components/JustificationModal";
import EditMaturityModal from "../../../components/EditMaturityModal";
import { EMPTY_LIST } from "src/common/constants";
import ApplicableReasonModal from "../../../components/ApplicableReasonModal";
import FilteringComponent from "../../../components/filtering-component";

import {
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from 'react-bootstrap';

const truncate = (text, max = 60) => {
    if (!text) return "-";
    return text.length > max ? text.slice(0, max) + "…" : text;
};

const controlTypeKey = "DORA";

const DORA = ({ t, lng, token, organization, user }) => {
    const [tableFilters, setTableFilters] = useState({
        pageIndex: 1,
        pageSize: 25,
        sortField: null,
        sortOrder: null
    });

    const [showApplicableReasonModal, setShowApplicableReasonModal] = useState(null);
    const [loadingApplicableIds, setLoadingApplicableIds] = useState([]);
    const [moreActionsDropDownOpen, setMoreActionsDropDownOpen] = useState(false);
    const [exportPDFInprocess, setExportPDFInprocess] = useState(false);
    const [exportPDFObjectUrl, setExportPDFObjectUrl] = useState(null);
    const [controlToEditJustification, setControlToEditJustification] = useState(null);
    const [controlToEditMaturityLevel, setControlToEditMaturityLevel] = useState(null);

    const toggleMoreActionsDropDown = () => setMoreActionsDropDownOpen(!moreActionsDropDownOpen)

    const downloadFile = ({
        id,
        title
    }) => {
        axios.get(`${API_URL_DOWNLOAD_APP_DOCUMENTS}/${id}/download`, {
            responseType: "arraybuffer",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            params: {},
            onDownloadProgress: (e) => {
                const {
                    total,
                    loaded
                } = e;

                const downloadedPercent = Math.round((loaded * 100) / total);

                if (downloadedPercent >= 100) { }
            },
        })
            .then((blob) => {
                const link = document.createElement("a");

                link.href = window.URL.createObjectURL(new Blob([blob.data]));
                link.setAttribute("download", `${title}.pdf`);

                document.body.appendChild(link);

                link.click();
                link.remove();
            })
            .catch(() => {
                toast(t("An error occurred while downloading the file."), {
                    type: "error"
                });
            });
    };

    const toggleApplicableMutation = useMutation({
        mutationFn: async ({ controlId, reason }) => {
            const service = ControlService.getInstance();
            return await service.toggleApplicable(controlId, { reason });
        },
        onSuccess: (_response, { controlId }) => {
            toast(t("Applicability updated successfully."), {
                type: "success"
            });

            handleFetchControlsListQuery.refetch();
            setShowApplicableReasonModal(null);
            setLoadingApplicableIds((currentState) => {
                return currentState.filter((cId) => cId !== controlId)
            })
        },
        onError: () => {
            toast(t("Failed to update applicability. Please try again."), {
                type: "error"
            });
        }
    });

    const handleToggleApplicable = useCallback(({ applicable, ...elseProps }) => {
        if (applicable) {
            setShowApplicableReasonModal(elseProps)
        }
        else {
            setLoadingApplicableIds((currentState) => ([...currentState, elseProps.id]))
            toggleApplicableMutation.mutate({
                controlId: elseProps.id
            });
        }
    }, [
        toggleApplicableMutation
    ]);

    const tableColumns = useMemo(() => {
        return [
            {
                dataField: "id",
                text: t("ID"),
                sort: true,
                key: 0,
                style: {
                    width: "60px"
                }
            },
            {
                key: 1,
                dataField: "id",
                text: t("Control Title"),
                sort: true,
                style: {
                    width: "200px"
                },
                formatter: (cell, {
                    id,
                    title,
                    description,
                    controlNumber
                }) => {
                    return (
                        <Link
                            to={`/admin/data-protection/controls/${id}/tasks`}
                        >
                            <span className="text-dark me-1">
                                {controlNumber}. {title}
                            </span>

                            <i
                                className="ri-information-line font-size-14 align-middle"
                                id={`control-${id}`}
                            >
                                <UncontrolledTooltip
                                    target={`control-${id}`}
                                    trigger="hover"
                                >
                                    {description}
                                </UncontrolledTooltip>
                            </i>
                        </Link>
                    );
                }
            },
            {
                dataField: "sectionTitle",
                text: t("Section"),
                sort: false,
                key: 2,
                style: {
                    width: "120px"
                }
            },
            {
                dataField: "subsectionTitle",
                text: t("Subsection"),
                sort: false,
                key: 2,
                style: {
                    width: "120px"
                }
            },
            {
                dataField: "tasks",
                text: t("Progress"),
                sort: false,
                key: 3,
                style: {
                    width: "300px",
                },
                formatter: (cell, {
                    id,
                    inProgressTasks,
                    closedTasks,
                    newTasks,
                    deletedTasks,
                    reviewTasks,
                    totalTasks
                }) => {

                    if (!totalTasks) {
                        return (
                            <span className="text-muted">
                                {t("No tasks available")}
                            </span>
                        )
                    }

                    const newTasksPercentage = Math.round(totalTasks > 0 ? (newTasks / totalTasks) * 100 : 0);
                    const inProgressTasksPercentage = Math.round(totalTasks > 0 ? (inProgressTasks / totalTasks) * 100 : 0);
                    const closedTasksPercentage = Math.round(totalTasks > 0 ? (closedTasks / totalTasks) * 100 : 0);
                    const deletedTasksPercentage = Math.round(totalTasks > 0 ? (deletedTasks / totalTasks) * 100 : 0);
                    const reviewTasksPercentage = Math.round(totalTasks > 0 ? (reviewTasks / totalTasks) * 100 : 0);

                    return (
                        <div>
                            <Progress
                                multi
                                style={{
                                    height: '12px'
                                }}
                            >
                                <Progress
                                    bar
                                    barClassName="bg-task-new"
                                    value={newTasksPercentage}
                                    id={`tooltip-new-tasks-${id}`}
                                >
                                    <span className="text-dark">
                                        {newTasksPercentage}%
                                    </span>
                                    <UncontrolledTooltip
                                        target={`tooltip-new-tasks-${id}`}
                                        placement="top"
                                    >
                                        {t('New')}: {newTasks} {t('of')} {totalTasks}
                                    </UncontrolledTooltip>
                                </Progress>
                                <Progress
                                    bar
                                    barClassName="bg-task-in-progress"
                                    value={inProgressTasksPercentage}
                                    id={`tooltip-in-progress-tasks-${id}`}
                                >
                                    <span>
                                        {inProgressTasksPercentage}%
                                    </span>
                                    <UncontrolledTooltip
                                        target={`tooltip-in-progress-tasks-${id}`}
                                        placement="top"
                                    >
                                        {t('In Progress')}: {inProgressTasks} {t('of')} {totalTasks}
                                    </UncontrolledTooltip>
                                </Progress>
                                <Progress
                                    bar
                                    barClassName="bg-task-closed"
                                    value={closedTasksPercentage}
                                    id={`tooltip-closed-tasks-${id}`}
                                >
                                    <span
                                        className="text-dark"
                                    >
                                        {closedTasksPercentage}%
                                    </span>
                                    <UncontrolledTooltip
                                        target={`tooltip-closed-tasks-${id}`}
                                        placement="top"
                                    >
                                        {t('Closed')}: {closedTasks} {t('of')} {totalTasks}
                                    </UncontrolledTooltip>
                                </Progress>
                                <Progress
                                    bar
                                    barClassName="bg-task-review"
                                    value={reviewTasksPercentage}
                                    id={`tooltip-review-tasks-${id}`}
                                >
                                    <span
                                        className="text-dark"
                                    >
                                        {reviewTasksPercentage}%
                                    </span>
                                    <UncontrolledTooltip
                                        target={`tooltip-review-tasks-${id}`}
                                        placement="top"
                                    >
                                        {t('Review')}: {reviewTasks} {t('of')} {totalTasks}
                                    </UncontrolledTooltip>
                                </Progress>
                                <Progress
                                    bar
                                    barClassName="bg-task-delete"
                                    value={deletedTasksPercentage}
                                    id={`tooltip-delete-tasks-${id}`}
                                >
                                    <span
                                        className="text-dark"
                                    >
                                        {deletedTasksPercentage}%
                                    </span>
                                    <UncontrolledTooltip
                                        target={`tooltip-delete-tasks-${id}`}
                                        placement="top"
                                    >
                                        {t('Deleted')}: {deletedTasks} {t('of')} {totalTasks}
                                    </UncontrolledTooltip>
                                </Progress>
                            </Progress>
                        </div>
                    );
                }
            },
            {
                dataField: "tasks",
                text: t("Related Tasks"),
                sort: false,
                key: 4,
                style: {
                    width: "200px",
                },
                formatter: (cell, {
                    id: controlId
                }) => {
                    return (
                        <div
                            style={{
                                display: 'inline-block',
                                width: '100%',
                                wordBreak: 'break-word',
                                whiteSpace: 'normal'
                            }}
                        >
                            {(cell || []).map(({
                                id,
                                name,
                                asset_id,
                                case_secondary_id,
                                risk_secondary_id,
                                secondary_id,
                                is_mitigation,
                                questionnaire
                            }, index) => {
                                return (
                                    <Link
                                        key={index}
                                        id={`control-${controlId}-task-${id}`}
                                        to={`/admin/data-protection/tasks/${id}/details?controlId=${controlId}`}
                                    >
                                        {formatTaskAndRiskId(
                                            "task",
                                            questionnaire === 'software' ? asset_id : case_secondary_id,
                                            risk_secondary_id ?? secondary_id,
                                            is_mitigation,
                                            questionnaire,
                                            secondary_id
                                        )}
                                        <span
                                            className="me-1"
                                            hidden={index === cell.length - 1}
                                        >
                                            {','}
                                        </span>

                                        <UncontrolledTooltip
                                            target={`control-${controlId}-task-${id}`}
                                            trigger="hover"
                                        >
                                            {name}
                                        </UncontrolledTooltip>
                                    </Link>
                                )
                            })}
                        </div>
                    )
                }
            },
            {
                dataField: "documents",
                text: t("Related Policies"),
                sort: false,
                key: 5,
                style: {
                    width: "200px",
                },
                formatter: (cell) => {
                    return (
                        <div
                            style={{
                                display: 'inline-block',
                                width: '100%',
                                wordBreak: 'break-word',
                                whiteSpace: 'normal'
                            }}
                        >
                            {(cell || []).map(({
                                id,
                                title,
                                type
                            }, index) => {
                                return (
                                    <span
                                        key={index}
                                    // className="btn-link"
                                    // onClick={() => downloadFile({ id, title })}
                                    // style={{ cursor: 'pointer' }}
                                    >
                                        {title}
                                        <span
                                            className="me-1"
                                            hidden={index === cell.length - 1}
                                        >
                                            {','}
                                        </span>
                                    </span>
                                )
                            })}
                        </div>
                    )
                }
            },
            {
                dataField: "mappings",
                text: t("Mapped Controls"),
                sort: false,
                key: "mappings",
                style: {
                    width: "280px",
                },
                formatter: (cell, row) => {
                    const relatedMappings = cell?.related || [];
            
                    if (!relatedMappings.length) {
                        return <span className="text-muted">-</span>;
                    }
            
                    const groupedMappings = relatedMappings.reduce((acc, mapping) => {
                        const framework = mapping.framework || t("Unknown");
                        if (!acc[framework]) {
                            acc[framework] = [];
                        }
                        acc[framework].push(mapping);
                        return acc;
                    }, {});
            
                    return (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "8px",
                                width: "100%",
                            }}
                        >
                            {Object.entries(groupedMappings).map(([framework, mappings]) => (
                                <div key={framework}>
                                    <div
                                        style={{
                                            fontWeight: 600,
                                            fontSize: "12px",
                                            marginBottom: "2px",
                                            color: "#6c757d",
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        {framework}
                                    </div>
            
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "2px",
                                        }}
                                    >
                                        {mappings.map((mapping, index) => {
                                            const tooltipId = `mapping-${row.id}-${framework}-${mapping.id}-${index}`;
            
                                            return (
                                                <div
                                                    key={mapping.id || index}
                                                    id={tooltipId}
                                                    style={{
                                                        wordBreak: "break-word",
                                                        whiteSpace: "normal",
                                                        lineHeight: "1.3",
                                                        cursor: "default",
                                                    }}
                                                >
                                                    {mapping.control_number}
            
                                                    <UncontrolledTooltip
                                                        target={tooltipId}
                                                        trigger="hover"
                                                    >
                                                        <div>
                                                            <strong>{framework}</strong>{" "}
                                                            {mapping.control_number}
                                                        </div>
                                                        <div>{mapping.title}</div>
                                                        <div>{t("Direction")}: {mapping.direction}</div>
                                                    </UncontrolledTooltip>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    );
                }
            },
            {
                dataField: "applicable",
                text: t("Applicable"),
                sort: false,
                key: 6,
                style: {
                    width: "80px"
                },
                formatter: (cellContent, row) => {
                    const isLoading = loadingApplicableIds.includes(row.id);

                    return (
                        <Button
                            color={cellContent ? 'success' : 'secondary'}
                            onClick={() => handleToggleApplicable(row)}
                            size="sm"
                            disabled={isLoading}
                            id={`applicable-btn-${row.id}`}
                        >
                            {isLoading ? (
                                <Spinner size="sm" color="white" />
                            ) : (
                                t(cellContent ? 'Yes' : 'No')
                            )}

                            <UncontrolledTooltip
                                target={`applicable-btn-${row.id}`}
                                trigger="hover"
                            >
                                {t(cellContent ? 'Click to mark as Not Applicable.' : 'Click to mark as Applicable.')}
                            </UncontrolledTooltip>
                        </Button>
                    )
                }
            },
            {
                dataField: "notApplicableReason",
                text: t("Justification"),
                sort: false,
                key: 7,
                style: {
                    width: "160px"
                },
                formatter: (cellContent, row) => {
                    return (
                        <div className="d-flex align-items-center gap-2">
                            <span title={cellContent}>
                                {truncate(cellContent)}
                            </span>

                            <Button
                                color="link"
                                size="sm"
                                className="p-0"
                                onClick={() => setControlToEditJustification(row)}
                                style={{ display: "inline-flex", alignItems: "center" }}
                                id={`edit-justification-btn-${row.id}`}
                            >
                                <RiEdit2Line size={16} />

                                <UncontrolledTooltip
                                    target={`edit-justification-btn-${row.id}`}
                                    trigger="hover"
                                >
                                    {t('Edit Justification')}
                                </UncontrolledTooltip>
                            </Button>
                        </div>
                    )
                }
            }
        ]
    }, [
        t,
        handleToggleApplicable,
        loadingApplicableIds
    ]);

    const handleFetchControlsListQuery = useQuery({
        queryKey: [
            "data-protection-controls-list",
            tableFilters,
            controlTypeKey,
            lng
        ],
        queryFn: async () => {
            const service = ControlService.getInstance();
            return await service.list({
                ...tableFilters,
                type: controlTypeKey
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while retrieving the controls."), {
                type: "error"
            });
        },
    });

    const handleFetchControlsListForExportQuery = useQuery({
        queryKey: [
            "data-protection-controls-list-for-export",
            controlTypeKey,
            lng
        ],
        queryFn: async () => {
            setExportPDFInprocess(true);

            const service = ControlService.getInstance();
            return await service.list({
                pageIndex: 1,
                pageSize: 100,
                type: controlTypeKey
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: false,
        onSuccess: async ({ controls }) => {
            let fileName = `Statement of Applicability.pdf`;

            const blob = await pdf(
                <SOAPdfExport
                    controls={controls}
                    organization={organization}
                    user={user}
                />
            ).toBlob();

            saveAs(blob, fileName);

            setExportPDFObjectUrl(URL.createObjectURL(blob));
        },
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while retrieving the controls."), {
                type: "error"
            });

            setExportPDFInprocess(false);
        },
    });

    const NoDataIndication = () =>
        handleFetchControlsListQuery.isFetched &&
            !handleFetchControlsListQuery.length ? (
            <div className="alert m-0" role="alert">
                <p
                    style={{
                        textAlign: "center",
                        marginBottom: 0,
                    }}
                >
                    {t(EMPTY_LIST)}
                </p>
            </div>
        ) : (
            <></>
        );

    const handleTableChange = (type, data) => {
        switch (type) {
            case "pagination":
                const { page, sizePerPage } = data
                setTableFilters({
                    ...tableFilters,
                    pageIndex: page,
                    pageSize: sizePerPage,
                });
                break;

            case 'sort': {
                const { sortField, sortOrder } = data
                setTableFilters({
                    ...tableFilters,
                    sortField: sortField,
                    sortOrder: sortOrder,
                });
                break
            }
            default:
                return false;
        }
    };

    const handleExportSOA = (e) => {
        e.preventDefault();
        e.stopPropagation();

        handleFetchControlsListForExportQuery.refetch();
    }

    const handleFilterChange = useCallback((newFilters) => {
        setTableFilters((currentFilters) => {
            return {
                ...currentFilters,
                ...newFilters,
                pageIndex: 1
            }
        });
    }, []);

    return (
        <>
            <Col sm="12" md="12" lg="8" className="mb-3 mt-3 d-flex justify-content-end">
                <FilteringComponent
                    onChange={handleFilterChange}
                />

                <Dropdown
                    isOpen={moreActionsDropDownOpen}
                    toggle={toggleMoreActionsDropDown}
                >
                    <DropdownToggle
                        caret
                        variant="outline-secondary"
                        outline
                    >
                        {t('Actions')}
                    </DropdownToggle>
                    <DropdownMenu
                        className="dropdown-menu-end"
                    >
                        <DropdownItem
                            onClick={handleExportSOA}
                        >
                            <div>
                                <i className="ri-download-2-line align-middle me-2" />
                                {t("Download SoA PDF")}
                            </div>
                        </DropdownItem>
                    </DropdownMenu>
                </Dropdown>
            </Col>

            <Col sm="12">
                <PaginationProvider
                    pagination={paginationFactory({
                        custom: true,
                        page: tableFilters.pageIndex,
                        sizePerPage: tableFilters.pageSize,
                        totalSize: parseInt(handleFetchControlsListQuery.data?.itemsCount || 0),
                        withFirstAndLast: false,
                        alwaysShowAllBtns: true,
                        prePageText: (
                            <span>
                                <i className="ri-arrow-left-s-line"></i> {t("Back")}
                            </span>
                        ),
                        nextPageText: (
                            <span>
                                {t("Next")} <i className="ri-arrow-right-s-line"></i>
                            </span>
                        ),
                        prePageTitle: t("Pre page"),
                        firstPageTitle: t("Next page"),
                        sizePerPageList: [
                            {
                                text: "25",
                                value: 25,
                            },
                            {
                                text: "50",
                                value: 50,
                            },
                        ],
                        showTotal: false,
                        paginationTotalRenderer: (from, to, size) => {
                            return (
                                <span className="react-bootstrap-table-pagination-total">
                                    {`${t('Total Items')} ${size}`}
                                </span>
                            )
                        },
                        hideSizePerPage: false,
                    })}
                >
                    {({ paginationProps, paginationTableProps }) => (
                        <>
                            <Row>
                                <Col sm="12">
                                    <BootstrapTable
                                        remote={{
                                            pagination: true,
                                            filter: false,
                                            sort: true,
                                            cellEdit: false,
                                            search: false,
                                        }}
                                        loading={
                                            handleFetchControlsListQuery.isFetching ||
                                            handleFetchControlsListQuery.isLoading
                                        }
                                        overlay={overlayFactory({
                                            spinner: (
                                                <Spinner
                                                    animation="border"
                                                    variant="primary"
                                                    size="md"
                                                />
                                            ),
                                            text: "Loading...",
                                        })}
                                        onTableChange={handleTableChange}
                                        defaultSorted={[]}
                                        keyField={"id"}
                                        responsive
                                        bordered={false}
                                        data={handleFetchControlsListQuery.data?.controls || []}
                                        striped={false}
                                        columns={tableColumns}
                                        wrapperClasses="table-responsive"
                                        classes={"table tpdd-table"}
                                        headerWrapperClasses={"thead-light"}
                                        style={{
                                            overflowX: "auto",
                                        }}
                                        noDataIndication={() => <NoDataIndication />}
                                        {...paginationTableProps}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col sm="12">
                                    <div className="tpdd-pagination-style-1">
                                        <PaginationTotalStandalone
                                            {...paginationProps}
                                        />

                                        <div>
                                            <PaginationListStandalone
                                                {...paginationProps}
                                            />

                                            <SizePerPageDropdownStandalone
                                                {...paginationProps}
                                            />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                </PaginationProvider>

                <ApplicableReasonModal
                    isOpen={showApplicableReasonModal}
                    toggle={() => setShowApplicableReasonModal(null)}
                    onSubmit={(payload) => toggleApplicableMutation.mutate({
                        ...payload,
                        controlId: showApplicableReasonModal.id
                    })}
                    controlName={showApplicableReasonModal?.title}
                    isSubmiting={toggleApplicableMutation.isLoading}
                />

                <ExportWaitingModal
                    isOpen={exportPDFInprocess}
                    toggle={() => {
                        setExportPDFInprocess(false);
                        setExportPDFObjectUrl(null);
                    }}
                    pdfObjectUrl={exportPDFObjectUrl}
                />

                <JustificationModal
                    isOpen={!!controlToEditJustification}
                    toggle={() => setControlToEditJustification(null)}
                    controlId={controlToEditJustification?.id}
                    value={controlToEditJustification?.notApplicableReason}
                    onSaved={handleFetchControlsListQuery.refetch}
                />

                <EditMaturityModal
                    isOpen={!!controlToEditMaturityLevel}
                    toggle={() => setControlToEditMaturityLevel(null)}
                    currentValue={controlToEditMaturityLevel?.maturityLevel}
                    controlId={controlToEditMaturityLevel?.id}
                    onSaved={handleFetchControlsListQuery.refetch}
                />
            </Col>
        </>
    )
}

const mapStatetoProps = (state) => {
    const { token, user } = state.Login;
    const { Organization } = state;

    return {
        token,
        user,
        organization: Organization,
    };
};

export default withNamespaces()(
    connect(mapStatetoProps)(
        DORA
    )
);